import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Network, FileSearch, ScrollText, UserCheck, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import ParticleField from '../components/ParticleField';
import FloatingLogo from '../components/FloatingLogo';
import { Reveal } from '../components/Footer';
import {
  brand, heroStats, problems, approach, whyHpp, services, eiomPrinciples,
} from '../mock';

const principleIcons = [ShieldCheck, Network, FileSearch, ScrollText, UserCheck];

export default function Home() {
  return (
    <div className="App">
      {/* HERO */}
      <section className="hero-dark relative overflow-hidden">
        <ParticleField density={1.1} />
        <div className="absolute inset-0 hidden md:block pointer-events-none">
          <FloatingLogo src={brand.logoTransparent} size={210} speed={0.5} vary />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 pt-40 pb-28 md:pt-48 md:pb-36">
          <div className="mx-auto max-w-[1300px] text-center">
            <div className="reveal in-view eyebrow text-[#c6a94b] mb-6">Enterprise Intelligence · Governed by design</div>
            <h1 className="font-serif leading-[1.1] text-white text-[30px] sm:text-[42px] lg:text-[50px] xl:text-[56px]">
              Turn AI ambition into <span className="text-brand-gradient">Governed Enterprise Capability</span>
            </h1>
            <p className="mt-8 mx-auto max-w-3xl text-[17px] leading-relaxed text-[#c4d0cd]">
              {brand.positioning}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-gold rounded-full h-12 px-7 text-[14px] font-semibold w-full sm:w-auto">
                  {brand.primaryCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/eiom">
                <Button variant="outline" className="rounded-full h-12 px-7 text-[14px] font-semibold w-full sm:w-auto bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white">
                  Explore EIOM
                </Button>
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl mx-auto">
              {heroStats.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-[34px] md:text-[40px] text-white">{s.value}</div>
                  <div className="mt-1 text-[12px] leading-snug text-[#94a6a2]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#faf9f5]" />
      </section>

      {/* POSITIONING STRIP */}
      <section className="relative overflow-hidden bg-[#faf9f5] py-16">
        <ParticleField density={0.5} variant="light" />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8 text-center">
          <Reveal>
            <p className="eyebrow text-[#1f5866]">Our principle &middot; what we deliver</p>
            <p className="mt-6 font-serif text-[30px] md:text-[42px] leading-snug text-[#22312f]">
              Evidence you can trust.
              <br className="hidden sm:block" /> Decisions you can govern.
              Solutions you can implement.
            </p>
            <div className="brand-rule mx-auto mt-7" />
          </Reveal>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="relative overflow-hidden bg-white py-24 border-y border-[#eceae2]">
        <ParticleField density={0.55} variant="light" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#c6a94b]">The problem</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">
              AI ambition is common. Governed Capability is rare.
            </h2>
            <p className="mt-5 mx-auto max-w-3xl text-[16px] leading-relaxed text-[#5a6668]">
              Most organisations can start with AI. Fewer can make it defensible, durable and accountable.
              <br />
              Herbert Project Partners closes that gap.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {problems.map((p, i) => (
              <Reveal key={p.title} delay={i * 90}>
                <div className="lift h-full rounded-2xl border border-[#eceae2] bg-[#faf9f5] p-8">
                  <div className="flex items-start gap-4">
                    <span className="font-serif text-[26px] text-[#c6a94b] leading-none">0{i + 1}</span>
                    <div>
                      <h3 className="font-serif text-[23px] text-[#22312f]">{p.title}</h3>
                      <p className="mt-3 text-[15px] leading-relaxed text-[#5a6668]">{p.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="relative overflow-hidden bg-[#faf9f5] py-24">
        <ParticleField density={0.55} variant="light" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#1f5866]">Our approach</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">
              From Problem to Sustained Capability
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {approach.map((a, i) => (
              <Reveal key={a.step} delay={i * 90}>
                <div className="relative h-full rounded-2xl bg-white border border-[#eceae2] p-7 lift">
                  <div className="eyebrow text-[#c6a94b]">{a.step}</div>
                  <h3 className="mt-4 font-serif text-[24px] text-[#22312f]">{a.title}</h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-[#5a6668]">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EIOM TEASER (dark, network) */}
      <section className="hero-dark relative overflow-hidden py-24">
        <ParticleField density={0.7} />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <p className="eyebrow text-[#c6a94b]">Proprietary IP</p>
              <h2 className="mt-4 font-serif text-[36px] md:text-[50px] leading-tight text-white">
                The Enterprise Intelligence Operating Model
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-[#c4d0cd] max-w-xl">
                EIOM is Herbert Project Partners’ operating model for governed, interoperable intelligence - built on evidence, auditability and human accountability. We publish the value and principles; the implementation stays proprietary.
              </p>
              <Link to="/eiom" className="mt-8 inline-flex">
                <Button className="btn-gold rounded-full h-11 px-6 text-[13px] font-semibold">
                  Explore EIOM <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Reveal>
            <Reveal delay={120}>
              <div className="grid gap-4 sm:grid-cols-2">
                {eiomPrinciples.map((p, i) => {
                  const Icon = principleIcons[i % principleIcons.length];
                  return (
                    <div key={p.title} className="rounded-xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm hover:bg-white/[0.07] transition-colors">
                      <Icon className="h-6 w-6 text-[#c6a94b]" />
                      <h3 className="mt-3 font-serif text-[19px] text-white">{p.title}</h3>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-[#a9b8b4]">{p.body}</p>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-white py-24 border-b border-[#eceae2]">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="flex flex-col items-center text-center gap-4">
            <div className="mx-auto max-w-4xl">
              <p className="eyebrow text-[#1f5866]">What we do</p>
              <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">
                Services that bring structure to AI Intelligence
              </h2>
            </div>
            <Link to="/services" className="nav-link text-[14px] font-semibold text-[#1f5866] whitespace-nowrap">
              View all services →
            </Link>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {services.map((s, i) => (
              <Reveal key={s.name} delay={i * 80}>
                <div className="lift h-full rounded-2xl border border-[#eceae2] p-8">
                  <span className="inline-block rounded-full bg-[#eef3f1] px-3 py-1 text-[11px] font-semibold tracking-wide text-[#1f5866]">{s.tag}</span>
                  <h3 className="mt-4 font-serif text-[26px] text-[#22312f]">{s.name}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#5a6668]">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY HPP */}
      <section className="relative overflow-hidden bg-[#faf9f5] py-24">
        <ParticleField density={0.55} variant="light" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#c6a94b]">Why Herbert Project Partners</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">
              Structure, Governance and Commercial Discipline
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-[#e7e5dd] bg-[#e7e5dd] md:grid-cols-2">
            {whyHpp.map((w) => (
              <div key={w.title} className="bg-white p-8 hover:bg-[#fcfbf7] transition-colors">
                <h3 className="font-serif text-[24px] text-[#22312f]">{w.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#5a6668]">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#faf9f5] py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <div className="hero-dark relative overflow-hidden rounded-3xl px-8 py-16 md:px-16 md:py-20">
              <ParticleField density={0.6} />
              <div className="relative z-10 mx-auto max-w-3xl text-center">
                <h2 className="font-serif text-[30px] md:text-[42px] leading-tight text-white">
                  Ready to move from ambition to capability?
                </h2>
                <p className="mt-5 mx-auto max-w-2xl text-[16px] leading-relaxed text-[#c4d0cd]">
                  Start with a focused discovery conversation. Tell us your challenge and we’ll suggest a governed, practical next step.
                </p>
                <Link to="/contact" className="mt-8 inline-flex">
                  <Button className="btn-gold rounded-full h-12 px-7 text-[14px] font-semibold">
                    {brand.primaryCta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
